import {
  track,
  type WalletSignerRecoveryAction,
  type WalletSignerRecoveryLocation
} from '@/lib/analytics';
import { reportGlobalError } from '@/lib/observability';
import type { WalletSignerRecoveryOutcome } from '@/providers/wallet-signer-health-provider';

interface RunWalletSignerRecoveryOptions {
  action: WalletSignerRecoveryAction;
  location: WalletSignerRecoveryLocation;
  operation: () => Promise<WalletSignerRecoveryOutcome>;
  route?: string;
}

/**
 * Records one explicit signer-recovery action without forwarding provider
 * errors, wallet names, or addresses. A failed recovery remains a safe UI
 * outcome; reporting failures must never replace the user's escape controls.
 */
export async function runWalletSignerRecovery({
  action,
  location,
  operation,
  route
}: RunWalletSignerRecoveryOptions): Promise<boolean> {
  const properties = { action, location } as const;
  track('app.wallet_signer_recovery_started', properties);

  let outcome: WalletSignerRecoveryOutcome;
  try {
    outcome = await operation();
  } catch {
    outcome = 'failed';
  }

  if (outcome === 'succeeded') {
    track('app.wallet_signer_recovery_succeeded', properties);
    return true;
  }

  if (outcome === 'rejected') {
    track('app.wallet_signer_recovery_rejected', properties);
    return false;
  }

  track('app.wallet_signer_recovery_failed', properties);
  const safeError = new Error('Wallet signer recovery failed');
  safeError.name = 'WalletSignerRecoveryError';
  void reportGlobalError(safeError, {
    route:
      route ??
      (typeof window === 'undefined' ? 'unknown' : window.location.pathname),
    feature: 'wallet-signer-recovery',
    action,
    location
  }).catch(() => {
    // Observability must never replace the recovery controls.
  });
  return false;
}
